import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import StaffNotation from '@/components/feature/StaffNotation';
import PianoKeyboard from '@/components/feature/PianoKeyboard';
import ScorePanel from '@/components/feature/ScorePanel';
import MidiPanel from '@/components/feature/MidiPanel';
import Button from '@/components/base/Button';
import Badge from '@/components/base/Badge';
import { useExerciseSession } from '@/hooks/useExerciseSession';
import { useMidiInput } from '@/hooks/useMidiInput';
import { getNote } from '@/lib/music/notes';
import { playMidi } from '@/lib/audio/synth';
import type { NoteStatsMap, ScoreState } from '@/lib/exercise/engine';
import { MASTERY_ACCURACY_GOAL, MASTERY_MIN_ATTEMPTS } from '@/lib/progress/gating';
import { nextLessonId, type Lesson } from '@/data/lessons';

/** Tempo, em segundos, de cada exercício no modo de resposta rápida. */
const QUICK_LIMIT = 8;

interface PracticeAreaProps {
  lesson: Lesson;
  noteStats: NoteStatsMap;
  onNoteResult: (noteId: string, correct: boolean) => void;
  onFinish: (score: ScoreState) => void;
  onAbort: (score: ScoreState) => void;
}

export default function PracticeArea({
  lesson,
  noteStats,
  onNoteResult,
  onFinish,
  onAbort,
}: PracticeAreaProps) {
  const session = useExerciseSession(lesson, { noteStats, onNoteResult, onFinish, onAbort });
  const isLineSpace = lesson.mode === 'line-space';
  const previewNoteId = lesson.notes?.[0] ?? 'C4';
  const currentNoteId = session.exercise?.noteId ?? previewNoteId;

  const handleMidiNote = useCallback(
    (midi: number) => {
      if (session.phase !== 'practice') return;
      playMidi(midi, 0.8);
      if (!isLineSpace) session.submitNote(midi);
    },
    [isLineSpace, session],
  );

  const midiInput = useMidiInput(handleMidiNote);

  const hearCurrent = () => {
    if (session.phase === 'intro') {
      playMidi(getNote(previewNoteId).midi, 1.1);
      return;
    }
    session.hearCurrent();
  };

  const [quickMode, setQuickMode] = useState(false);
  const [timeLeft, setTimeLeft] = useState(QUICK_LIMIT);
  const timeoutRef = useRef(session.timeout);

  useEffect(() => {
    timeoutRef.current = session.timeout;
  });

  const quickAvailable = session.score.correct >= 5 || session.score.bestStreak >= 3;

  useEffect(() => {
    if (!quickMode || isLineSpace || session.phase !== 'practice' || session.feedback) {
      return undefined;
    }
    const startedAt = Date.now();
    setTimeLeft(QUICK_LIMIT);
    const id = window.setInterval(() => {
      const remaining = QUICK_LIMIT - (Date.now() - startedAt) / 1000;
      if (remaining <= 0) {
        setTimeLeft(0);
        window.clearInterval(id);
        timeoutRef.current();
      } else {
        setTimeLeft(remaining);
      }
    }, 100);
    return () => window.clearInterval(id);
  }, [quickMode, isLineSpace, session.phase, session.feedback, session.exercise]);

  const showNames = lesson.id === 1 || session.feedback?.type === 'wrong';
  const next = nextLessonId(lesson.id);

  const noteMidis = (lesson.notes ?? []).map((id) => getNote(id).midi);
  const keyStart = noteMidis.length > 0 ? Math.min(48, Math.min(...noteMidis) - 2) : 48;
  const keyEnd = noteMidis.length > 0 ? Math.max(72, Math.max(...noteMidis) + 2) : 72;
  const showKeyNames = lesson.id <= 5 || lesson.id === 31 || lesson.id === 32;

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="flex flex-col gap-6">
        {/* Explicação antes do exercício */}
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
          <div className="flex items-center gap-2">
            <i className="ri-lightbulb-line w-5 h-5 flex items-center justify-center text-primary-700" />
            <span className="font-label text-sm font-bold text-primary-800">Antes de praticar</span>
          </div>
          <p className="mt-2 text-sm text-foreground-800">{lesson.explanation}</p>
          {lesson.hint ? (
            <p className="mt-2 text-sm text-foreground-600">
              <span className="font-semibold">Dica:</span> {lesson.hint}
            </p>
          ) : null}
          <p className="mt-3 rounded-md bg-background-50 px-3 py-2 text-xs text-foreground-700">
            <span className="font-semibold">Critério de domínio:</span> completar pelo menos{' '}
            {MASTERY_MIN_ATTEMPTS} variações e manter {MASTERY_ACCURACY_GOAL}% ou mais de precisão.
            Todas as notas da etapa precisam aparecer. Se errar, o sistema continua criando novas
            variações até você recuperar a meta.
          </p>
          {lesson.notes && lesson.notes.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-xs font-label font-semibold text-foreground-500">
                Notas desta aula:
              </span>
              {lesson.notes.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center rounded-full bg-background-50 px-2.5 py-0.5 text-xs font-label font-semibold text-foreground-800"
                >
                  {getNote(id).label}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* Área do exercício */}
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
          {session.phase === 'done' ? (
            <div className="flex flex-col items-center py-6 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-medal-line text-3xl" />
              </span>
              <h3 className="mt-5 font-heading text-xl font-bold text-foreground-950">
                Aula dominada!
              </h3>
              <p className="mt-2 max-w-md text-sm text-foreground-700">
                Você atingiu {MASTERY_ACCURACY_GOAL}% ou mais depois de pelo menos{' '}
                {MASTERY_MIN_ATTEMPTS} variações. A próxima aula foi desbloqueada.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Button variant="outline" size="lg" icon="ri-refresh-line" onClick={session.startPractice}>
                  Refazer aula
                </Button>
                {next ? (
                  <Link to={`/aulas/${next}`} className="contents">
                    <Button variant="primary" size="lg" icon="ri-arrow-right-line">
                      Próxima aula
                    </Button>
                  </Link>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Badge tone="neutral" icon="ri-eye-line">
                  {session.phase === 'intro' ? 'Prévia da nota' : 'Leia e toque'}
                </Badge>
                <span className="text-xs font-label font-semibold text-foreground-500">
                  {session.phase === 'intro'
                    ? `Meta: ${MASTERY_ACCURACY_GOAL}% após ${MASTERY_MIN_ATTEMPTS}+ variações`
                    : `Precisão: ${session.accuracy}% · combo: ${session.streak}`}
                </span>
              </div>

              {!isLineSpace ? (
                <div className="mt-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="flex items-center gap-2 text-xs font-label font-semibold text-foreground-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={quickMode}
                        disabled={!quickAvailable}
                        onChange={(event) => setQuickMode(event.target.checked)}
                        className="h-4 w-4 accent-primary-500"
                      />
                      Modo de resposta rápida (opcional)
                    </label>
                    {quickMode && session.phase === 'practice' && !session.feedback ? (
                      <span className="text-xs font-label font-semibold text-primary-700">
                        {timeLeft.toFixed(1)}s
                      </span>
                    ) : (
                      <span className="text-xs text-foreground-400">
                        {quickAvailable
                          ? 'Sem limite de tempo'
                          : 'Disponível depois de alguns acertos'}
                      </span>
                    )}
                  </div>
                  {quickMode && session.phase === 'practice' && !session.feedback ? (
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                      <div
                        className="h-full rounded-full bg-primary-500 transition-[width] duration-100"
                        style={{ width: `${Math.max(0, (timeLeft / QUICK_LIMIT) * 100)}%` }}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="mt-4 rounded-lg bg-background-100 p-3 md:p-4">
                <StaffNotation
                  notes={[currentNoteId]}
                  highlightIndex={session.phase === 'intro' ? 0 : undefined}
                  feedbackIndex={session.feedback ? 0 : undefined}
                  feedbackType={session.feedback?.type ?? null}
                  showNoteNames={showNames}
                  space={26}
                />
              </div>

              {/* Feedback */}
              <div className="mt-4 min-h-[52px]">
                {session.feedback ? (
                  <div
                    className={`flex items-start gap-3 rounded-lg px-4 py-3 text-sm animate-pop ${
                      session.feedback.type === 'correct'
                        ? 'bg-accent-100 text-accent-900'
                        : 'bg-secondary-100 text-secondary-900'
                    }`}
                  >
                    <i
                      className={`${
                        session.feedback.type === 'correct' ? 'ri-check-line' : 'ri-information-line'
                      } w-5 h-5 mt-0.5 flex items-center justify-center`}
                    />
                    <span>{session.feedback.message}</span>
                  </div>
                ) : (
                  <p className="text-sm text-foreground-500">
                    {isLineSpace
                      ? 'Observe a nota e escolha: ela está em uma linha ou em um espaço?'
                      : session.phase === 'intro'
                        ? 'Toque em "Praticar" para começar os exercícios.'
                        : 'Toque a tecla correspondente no teclado abaixo.'}
                  </p>
                )}
              </div>

              {isLineSpace ? (
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Button
                    variant="outline"
                    size="lg"
                    icon="ri-subtract-line"
                    disabled={session.phase !== 'practice'}
                    onClick={() => session.submitLineSpace('line')}
                  >
                    Está em uma linha
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    icon="ri-space"
                    disabled={session.phase !== 'practice'}
                    onClick={() => session.submitLineSpace('space')}
                  >
                    Está em um espaço
                  </Button>
                </div>
              ) : (
                <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-3">
                  <PianoKeyboard
                    startMidi={keyStart}
                    endMidi={keyEnd}
                    highlightMidis={session.phase === 'intro' && lesson.id === 1 ? [getNote('C4').midi] : []}
                    specialLabels={lesson.id === 1 ? { [getNote('C4').midi]: 'Dó central' } : {}}
                    showNoteNames={showKeyNames}
                    pressedMidi={session.lastAnswerMidi}
                    feedback={
                      session.feedback && session.lastAnswerMidi !== null
                        ? { midi: session.lastAnswerMidi, type: session.feedback.type }
                        : null
                    }
                    onKeyDown={(midi) => {
                      if (session.phase !== 'practice') return;
                      session.submitNote(midi);
                    }}
                  />
                  <p className="mt-2 text-center text-xs text-foreground-500 md:hidden">
                    Deslize o teclado para os lados para ver todas as teclas.
                  </p>
                </div>
              )}

              {/* Controles */}
              <div className="mt-5 flex flex-wrap gap-2.5">
                <Button variant="outline" icon="ri-volume-up-line" onClick={hearCurrent}>
                  Ouvir
                </Button>
                <Button variant="primary" icon="ri-play-line" onClick={session.startPractice}>
                  {session.phase === 'intro' ? 'Praticar' : 'Reiniciar'}
                </Button>
                <Button
                  variant="outline"
                  icon="ri-restart-line"
                  disabled={session.phase !== 'practice'}
                  onClick={session.retry}
                >
                  Repetir
                </Button>
                <Button
                  variant="outline"
                  icon="ri-arrow-right-line"
                  disabled={session.phase !== 'practice'}
                  onClick={session.skip}
                >
                  Próximo
                </Button>
                {session.phase === 'practice' ? (
                  <Button variant="ghost" icon="ri-arrow-go-back-line" onClick={session.reset}>
                    Voltar ao início
                  </Button>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Coluna lateral */}
      <aside className="flex flex-col gap-6">
        <ScorePanel
          score={session.score}
          accuracy={session.accuracy}
          streak={session.streak}
          streakGoal={session.streakGoal}
        />

        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="font-label text-sm font-bold text-foreground-800">
            Notas que ainda precisam aparecer
          </span>
          <div className="mt-3 flex flex-wrap gap-2">
            {session.missingNotes.length === 0 ? (
              <span className="flex items-center gap-1.5 text-sm text-accent-700">
                <i className="ri-checkbox-circle-fill w-4 h-4 flex items-center justify-center" />
                Todas já apareceram nesta sequência.
              </span>
            ) : (
              session.missingNotes.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-label font-semibold text-secondary-800"
                >
                  {getNote(id).label}
                </span>
              ))
            )}
          </div>
        </div>

        {session.lastError ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-4">
            <div className="flex items-center gap-2">
              <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center text-secondary-700" />
              <span className="font-label text-sm font-bold text-secondary-900">
                Motivo do último erro
              </span>
            </div>
            <p className="mt-2 text-sm text-foreground-800">{session.lastError}</p>
          </div>
        ) : null}

        {!isLineSpace ? (
          <MidiPanel
            supported={midiInput.supported}
            status={midiInput.status}
            devices={midiInput.devices}
            selectedId={midiInput.selectedId}
            onSelect={midiInput.setSelectedId}
            onConnect={midiInput.connect}
            onDisconnect={midiInput.disconnect}
          />
        ) : null}
      </aside>
    </div>
  );
}